import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Grid,
  Typography,
  Paper,
} from "@mui/material";
import { NotificationManager } from "react-notifications";
import { useNavigate, useParams } from "react-router-dom";
import CommonLoader from "../../common/CommonLoader";
import {
  SaveOutsourcedAnalysis,
  UpdateOutsourcedAnalysis,
  GetOutsourcedAnalysisById,
} from "../../../services/OutsourcedAnalysisService";

const AddOutsourcedAnalysis = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    orderNumber: "",
    sampleNumber: "",
    analysisByLab: "",
    testCode: "",
    analyticalValue: "",
    unit: "",
    additionalComments: "",

    // 🔹 New fields
    analyteName: "",
    resultConcentration: "",
    reportingLimit: "",
    dilutionFactor: "",
    eliSampleLabId: "",
    eurofinsSampleLabId: "",
    analysisDateTime: "",
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (id) {
      fetchAnalysisData(parseInt(id, 10));
    }
  }, [id]);

  const fetchAnalysisData = async (analysisId) => {
    setLoading(true);
    try {
      const response = await GetOutsourcedAnalysisById(analysisId);
      if (response?.results) {
        setFormData({
          ...response.results,
          analysisDateTime: response.results.analysisDateTime
            ? response.results.analysisDateTime.substring(0, 16)
            : "",
        });
      } else {
        NotificationManager.error("Failed to fetch analysis data");
      }
    } catch {
      NotificationManager.error("Error fetching analysis data");
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.orderNumber)
      newErrors.orderNumber = "Order number is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const analysisData = {
      ...formData,
      orderNumber: parseInt(formData.orderNumber, 10),
      sampleNumber: formData.sampleNumber
        ? parseInt(formData.sampleNumber, 10)
        : null,
      dilutionFactor: formData.dilutionFactor
        ? parseFloat(formData.dilutionFactor)
        : null,
      analysisDateTime: formData.analysisDateTime || null,
      id: id ? parseInt(id, 10) : undefined,
    };

    setLoading(true);
    try {
      if (id) {
        await UpdateOutsourcedAnalysis(analysisData);
        NotificationManager.success("Analysis updated successfully");
      } else {
        await SaveOutsourcedAnalysis(analysisData);
        NotificationManager.success("Analysis added successfully");
      }
      navigate("/outsourced-analysis");
    } catch (error) {
      NotificationManager.error(error.message || "Error saving analysis");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleCancel = () => navigate("/outsourced-analysis");

  return (
    <>
      <Paper elevation={3} sx={{ padding: 3, margin: "auto" }}>
        <Typography variant="h5" gutterBottom>
          {id ? "Edit Outsourced Analysis" : "Add New Outsourced Analysis"}
        </Typography>

        <Grid container spacing={2}>
          {/* Order & Sample */}
          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Order Number"
              type="number"
              value={formData.orderNumber}
              onChange={handleChange("orderNumber")}
              size="small"
              fullWidth
              required
              error={!!errors.orderNumber}
              helperText={errors.orderNumber}
            />
          </Grid>

          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Sample Number"
              type="number"
              value={formData.sampleNumber || ""}
              onChange={handleChange("sampleNumber")}
              size="small"
              fullWidth
            />
          </Grid>

          {/* Lab Info */}
          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Analysis By Lab"
              value={formData.analysisByLab || ""}
              onChange={handleChange("analysisByLab")}
              size="small"
              fullWidth
            />
          </Grid>

          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Test Code"
              value={formData.testCode || ""}
              onChange={handleChange("testCode")}
              size="small"
              fullWidth
            />
          </Grid>

          {/* Analytical Data */}
          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Analyte Name"
              value={formData.analyteName || ""}
              onChange={handleChange("analyteName")}
              size="small"
              fullWidth
            />
          </Grid>

          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Result / Concentration"
              value={formData.resultConcentration || ""}
              onChange={handleChange("resultConcentration")}
              size="small"
              fullWidth
            />
          </Grid>

          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Unit"
              value={formData.unit || ""}
              onChange={handleChange("unit")}
              size="small"
              fullWidth
            />
          </Grid>

          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Reporting Limit"
              value={formData.reportingLimit || ""}
              onChange={handleChange("reportingLimit")}
              size="small"
              fullWidth
            />
          </Grid>

          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Dilution Factor"
              type="number"
              value={formData.dilutionFactor || ""}
              onChange={handleChange("dilutionFactor")}
              size="small"
              fullWidth
            />
          </Grid>

          {/* Sample IDs */}
          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="ELI Sample Lab ID"
              value={formData.eliSampleLabId || ""}
              onChange={handleChange("eliSampleLabId")}
              size="small"
              fullWidth
            />
          </Grid>

          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Eurofins Sample Lab ID"
              value={formData.eurofinsSampleLabId || ""}
              onChange={handleChange("eurofinsSampleLabId")}
              size="small"
              fullWidth
            />
          </Grid>

          {/* Analysis Date */}
          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Analysis Date / Time"
              type="datetime-local"
              value={formData.analysisDateTime || ""}
              onChange={handleChange("analysisDateTime")}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Comments */}
          <Grid item xs={12}>
            <TextField
              label="Additional Comments"
              value={formData.additionalComments || ""}
              onChange={handleChange("additionalComments")}
              size="small"
              fullWidth
              multiline
              rows={3}
            />
          </Grid>

          {/* Actions */}
          <Grid item xs={12} container justifyContent="flex-end" spacing={2}>
            <Grid item>
              <Button onClick={handleCancel} variant="outlined" color="secondary">
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button onClick={handleSave} variant="contained" color="primary">
                {id ? "Update" : "Save"}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      <CommonLoader loading={loading} />
    </>
  );
};

export default AddOutsourcedAnalysis;